import EmptyStateProps from './EmptyStateProps';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import folderStore, {
    ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID,
    PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID,
} from 'owa-folders';
import selectFocusedViewFilter from 'owa-mail-list-actions/lib/actions/selectFocusedViewFilter';
import strings from 'owa-strings/owa-mail-strings';
import ViewFilter from 'owa-service/lib/contract/ViewFilter';
import { clearSearchScope } from 'owa-mail-search';
import { getFolderIdForSelectedNode } from 'owa-mail-folder-forest-store';
import { getIsLightBaseTheme } from 'owa-theme';
import { isFocusedInboxEnabled } from 'owa-mail-list-actions/lib/utils/FocusedInboxUtil';
import { logVerboseUsage } from 'owa-analytics';
import { SearchTableQuery } from 'owa-mail-list-search';
import { ActionSource } from 'owa-mail-store';
import { MailListViewState } from 'owa-mail-store/lib/store/schema/MailListViewState';
import { select } from 'satcheljs/lib/legacy';
import { trace } from 'owa-trace';
import {
    getFocusedFilterForTable,
    getViewFilterForTable,
    LoadErrorStatus,
    TableQuery,
    TableView,
    listViewStore,
    TableQueryType,
} from 'owa-mail-list-store';
import {
    SearchScopeKind,
    PrimaryMailboxSearchScope,
    ArchiveMailboxSearchScope,
} from 'owa-search-service';

let EMPTY_INBOX_SVG;
let EMPTY_FLAGGED_SVG;
let EMPTY_TOME_SVG;
let EMPTY_UNREAD_SVG;
let EMPTY_MENTIONS_SVG;
let EMPTY_ATTACHMENTS_SVG;
let EMPTY_SEARCH_SVG;
let EMPTY_JUNK_SVG;
let EMPTY_DRAFT_SVG;
let EMPTY_DELETED_SVG;
let EMPTY_FOLDER_SVG;
let LOADING_ERROR__SVG;
let ACCESS_ERROR_SVG;

export interface GetMailListEmptyStateState {
    tableView: TableView;
}

/**
 * initialize the svgs icons
 */
function initailizeDisplayIconsForEmptyList() {
    if (getIsLightBaseTheme()) {
        // colored svgs that goes with the blue base theme
        EMPTY_INBOX_SVG = require('owa-empty-state/lib/svg/colored/emptyInbox.svg');
        EMPTY_FLAGGED_SVG = require('owa-empty-state/lib/svg/colored/emptyFlagged.svg');
        EMPTY_TOME_SVG = require('owa-empty-state/lib/svg/colored/emptyToMe.svg');
        EMPTY_UNREAD_SVG = require('owa-empty-state/lib/svg/colored/emptyUnread.svg');
        EMPTY_MENTIONS_SVG = require('owa-empty-state/lib/svg/colored/emptyMentions.svg');
        EMPTY_ATTACHMENTS_SVG = require('owa-empty-state/lib/svg/colored/emptyAttachments.svg');
        EMPTY_SEARCH_SVG = require('owa-empty-state/lib/svg/colored/emptySearch.svg');
        EMPTY_JUNK_SVG = require('owa-empty-state/lib/svg/colored/emptyJunkFolder.svg');
        EMPTY_DRAFT_SVG = require('owa-empty-state/lib/svg/colored/emptyDraftFolder.svg');
        EMPTY_DELETED_SVG = require('owa-empty-state/lib/svg/colored/emptyDeletedItemsFolder.svg');
        EMPTY_FOLDER_SVG = require('owa-empty-state/lib/svg/colored/emptyFolder.svg');
        LOADING_ERROR__SVG = require('owa-empty-state/lib/svg/colored/loadingError.svg');
        ACCESS_ERROR_SVG = require('owa-empty-state/lib/svg/colored/accessError.svg');
    } else {
        // gray scale svgs
        EMPTY_INBOX_SVG = require('owa-empty-state/lib/svg/gray/emptyInbox.svg');
        EMPTY_FLAGGED_SVG = require('owa-empty-state/lib/svg/gray/emptyFlagged.svg');
        EMPTY_TOME_SVG = require('owa-empty-state/lib/svg/gray/emptyToMe.svg');
        EMPTY_UNREAD_SVG = require('owa-empty-state/lib/svg/gray/emptyUnread.svg');
        EMPTY_MENTIONS_SVG = require('owa-empty-state/lib/svg/gray/emptyMentions.svg');
        EMPTY_ATTACHMENTS_SVG = require('owa-empty-state/lib/svg/gray/emptyAttachments.svg');
        EMPTY_SEARCH_SVG = require('owa-empty-state/lib/svg/gray/emptySearch.svg');
        EMPTY_JUNK_SVG = require('owa-empty-state/lib/svg/gray/emptyJunkFolder.svg');
        EMPTY_DRAFT_SVG = require('owa-empty-state/lib/svg/gray/emptyDraftFolder.svg');
        EMPTY_DELETED_SVG = require('owa-empty-state/lib/svg/gray/emptyDeletedItemsFolder.svg');
        EMPTY_FOLDER_SVG = require('owa-empty-state/lib/svg/gray/emptyFolder.svg');
        LOADING_ERROR__SVG = require('owa-empty-state/lib/svg/gray/loadingError.svg');
        ACCESS_ERROR_SVG = require('owa-empty-state/lib/svg/gray/accessError.svg');
    }
}

/**
 * Get the empty list view display properties if user is in search mode
 * @param emptyStateProps the empty state display properties
 * @param tableQuery the table query
 */
async function getEmptyListViewDisplayPropertiesForSearch(
    emptyStateProps: EmptyStateProps,
    tableQuery: TableQuery
) {
    emptyStateProps.icon = EMPTY_SEARCH_SVG;
    emptyStateProps.mainMessage = strings.noSearchResults;
    emptyStateProps.detailMessage = strings.noSearchResultsDetailMessage;

    // Display the option to clean the search folder scope when the selected scope is not a root folder of any mailbox.
    const searchTableQuery = tableQuery as SearchTableQuery;
    const searchScopeKind = searchTableQuery.searchScope.kind;
    if (
        searchScopeKind == SearchScopeKind.PrimaryMailbox ||
        searchScopeKind == SearchScopeKind.ArchiveMailbox
    ) {
        const folderName = folderIdToName(
            (searchTableQuery.searchScope as PrimaryMailboxSearchScope | ArchiveMailboxSearchScope)
                .folderId
        );
        if (
            folderName != PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID &&
            folderName != ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID
        ) {
            emptyStateProps.linkMessage =
                searchScopeKind == SearchScopeKind.ArchiveMailbox
                    ? strings.noSearchResultsInArchiveMailboxResetFolderScopeLink
                    : strings.noSearchResultsResetFolderScopeLink;
            emptyStateProps.linkCommand = (actionSource: ActionSource) => {
                clearSearchScope(actionSource, true /* shouldStartSearch */, searchScopeKind);
            };
        }
    }
}

/**
 * Get the empty state display properties
 * @param showSearchEmptyState whether to show empty state for search
 * @param clearFilterCommand the command that resets the view filter to default view filter
 * @param viewFilter the current view filter
 * @param focusedFilter the focused view fitler
 * @param tableQuery the table query
 */
function getEmptyStateDisplayProperties(
    showSearchEmptyState: boolean,
    clearFilterCommand: (actionSource: ActionSource) => void,
    viewFilter: ViewFilter,
    focusedFilter: FocusedViewFilter,
    tableQuery: TableQuery
): EmptyStateProps {
    const emptyStateProps: EmptyStateProps = { mainMessage: null };

    if (showSearchEmptyState) {
        getEmptyListViewDisplayPropertiesForSearch(emptyStateProps, tableQuery);
    } else if (viewFilter == 'All') {
        getEmptyListViewDisplayPropertiesForAllFilter(
            getFolderIdForSelectedNode(),
            emptyStateProps,
            focusedFilter
        );
    } else {
        getEmptyListViewDisplayPropertiesForFilters(
            viewFilter,
            clearFilterCommand,
            emptyStateProps
        );
    }

    return emptyStateProps;
}

/**
 * Calculate the mail list display properties based on the list view state
 * @param tableViewId tableView id
 * @param stateType the mail list view state type
 * @param clearFilterCommand the command that clears view filter
 * @param reloadCommand the command that reloads the selected list view
 */

export default select<GetMailListEmptyStateState>({
    tableView: (tableViewId: string) => listViewStore.tableViews.get(tableViewId),
})(function getDisplayPropertiesForEmptyList(
    tableViewId: string,
    stateType: MailListViewState,
    clearFilterCommand: (actionSource: ActionSource) => void,
    reloadCommand: (actionSource: ActionSource) => void,
    state?: GetMailListEmptyStateState
) {
    let mailListState: EmptyStateProps = { mainMessage: null };
    const tableView = state.tableView;
    const scenarioType = (tableView.tableQuery as SearchTableQuery).scenarioType;
    // For persona scenario we don't want to show search empty state but instead normal folder empty state
    const showSearchEmptyState =
        tableView.tableQuery.type === TableQueryType.Search &&
        scenarioType !== 'persona' &&
        scenarioType !== 'privateDistributionList';
    const isGroup = tableView.tableQuery.type == TableQueryType.Group;
    const viewFilter = getViewFilterForTable(tableView);
    const focusedFilter = getFocusedFilterForTable(tableView);

    initailizeDisplayIconsForEmptyList();

    switch (stateType) {
        case MailListViewState.Error:
            mailListState = getDisplayPropertiesForError(
                tableView.loadErrorStatus,
                showSearchEmptyState,
                isGroup,
                reloadCommand
            );
            break;

        case MailListViewState.Loading:
            mailListState = {
                mainMessage: showSearchEmptyState ? strings.searching : strings.loadingFolder,
                icon: showSearchEmptyState ? EMPTY_SEARCH_SVG : EMPTY_FOLDER_SVG,
            };
            break;

        case MailListViewState.Empty:
            mailListState = getEmptyStateDisplayProperties(
                showSearchEmptyState,
                clearFilterCommand,
                viewFilter,
                focusedFilter,
                tableView.tableQuery
            );
            break;

        default:
            trace.error('Unrecognized empty state');
            break;
    }

    logVerboseUsage('TnS_MailListEmptyStateShownEvent', [
        stateType,
        showSearchEmptyState,
        viewFilter,
        focusedFilter,
    ]);

    return mailListState;
});

/**
 * Get the display properties when list view is empty because an error occurred while loading
 * @param error LoadErrorStatus enum
 * @param showSearchError whether to show error for search
 * @param reloadCommand the command that reloads the selected list view
 */
function getDisplayPropertiesForError(
    error: LoadErrorStatus,
    showSearchError: boolean,
    isGroup: boolean,
    reloadCommand: (actionSource: ActionSource) => void
): EmptyStateProps {
    switch (error) {
        case LoadErrorStatus.AccessDenied:
            return {
                mainMessage: isGroup ? strings.groupFolderAccessDenied : strings.accessDeniedText,
                icon: ACCESS_ERROR_SVG,
            };
        default:
            return {
                mainMessage: strings.mailListErrorMessage,
                icon: LOADING_ERROR__SVG,
                linkMessage: !showSearchError && strings.mailListErrorLinkMessage,
                linkCommand: !showSearchError && reloadCommand,
            };
    }
}

/**
 * Get the display properties when list view is empty and user is in 'All' pivot and in Focused or Other
 * @param focusedFilter focused or other
 * @param inboxId inbox id
 * @param emptyStateProps the state which is used to calculate the mail list display properties
 */
export function getEmptyListViewDisplayPropertiesForAllFiltersFocusedOther(
    focusedFilter: FocusedViewFilter,
    inboxId: string,
    emptyStateProps: EmptyStateProps
) {
    const isFocused = focusedFilter == FocusedViewFilter.Focused;
    emptyStateProps.icon = EMPTY_INBOX_SVG;

    if (isFocused) {
        const inboxFolder = folderStore.folderTable.get(inboxId);
        emptyStateProps.mainMessage = strings.focusedEmptyStateMessageText;

        if (inboxFolder.TotalCount > 0) {
            emptyStateProps.linkMessage = strings.focusedEmptyStateLinkMessageText;
            emptyStateProps.linkCommand = (actionSource: ActionSource) => {
                selectFocusedViewFilter(FocusedViewFilter.Other, actionSource);
            };
        }
    } else {
        emptyStateProps.mainMessage = strings.otherEmptyStateMessageText;
        emptyStateProps.linkMessage = strings.otherEmptyStateLinkMessageText;
        emptyStateProps.linkCommand = (actionSource: ActionSource) => {
            selectFocusedViewFilter(FocusedViewFilter.Focused, actionSource);
        };
    }
}

/**
 * Get the display properties when list view is empty and user is in 'All' pivot and in a regular folder (not Focused or Other)
 * @param selectedFolderId selected folder id. Can be null if a persona was selected with no associated searchFolder
 * @param emptyStateProps the state which is used to calculate the mail list display properties
 */
function getEmptyListViewDisplayPropertiesForAllFiltersRegularFolders(
    selectedFolderId: string,
    emptyStateProps: EmptyStateProps
) {
    const folderName = selectedFolderId ? folderIdToName(selectedFolderId) : null;
    let mainMessage = strings.folderIsEmpty;
    let icon = EMPTY_FOLDER_SVG;

    switch (folderName) {
        case 'inbox':
            mainMessage = strings.inboxIsEmpty;
            icon = EMPTY_INBOX_SVG;
            break;
        case 'junkemail':
            icon = EMPTY_JUNK_SVG;
            break;
        case 'deleteditems':
            icon = EMPTY_DELETED_SVG;
            break;
        case 'drafts':
            mainMessage = strings.draftsIsEmpty;
            icon = EMPTY_DRAFT_SVG;
            break;
    }

    emptyStateProps.mainMessage = mainMessage;
    emptyStateProps.detailMessage = null;
    emptyStateProps.icon = icon;
}

/**
 * Get the display properties when list view is empty and user is in 'All' pivot
 * @param selectedFolderId selected folder id
 * @param emptyStateProps the state which is used to calculate the mail list display properties
 * @param focusedFilter the focused fitler of the table
 */
export function getEmptyListViewDisplayPropertiesForAllFilter(
    selectedFolderId: string,
    emptyStateProps: EmptyStateProps,
    focusedFilter: FocusedViewFilter
) {
    const isFocusedOther = isFocusedInboxEnabled() && focusedFilter !== FocusedViewFilter.None;

    if (isFocusedOther) {
        getEmptyListViewDisplayPropertiesForAllFiltersFocusedOther(
            focusedFilter,
            selectedFolderId,
            emptyStateProps
        );
    } else {
        getEmptyListViewDisplayPropertiesForAllFiltersRegularFolders(
            selectedFolderId,
            emptyStateProps
        );
    }
}

/**
 * Get the empty state properties based on the view filter
 * @param viewFilter the view filter
 * @param clearFilterCommand the clears filter command
 * @param emptyStateProps the state which is used to calculate the mail list display properties
 */
export function getEmptyListViewDisplayPropertiesForFilters(
    viewFilter: string,
    clearFilterCommand: (actionSource: ActionSource) => void,
    emptyStateProps: EmptyStateProps
) {
    let mainMessage = strings.folderIsEmpty;
    let detailMessage = null;
    let icon = EMPTY_FOLDER_SVG;

    switch (viewFilter) {
        case 'Unread':
            mainMessage = strings.unreadIsEmpty;
            icon = EMPTY_UNREAD_SVG;
            emptyStateProps.linkMessage = strings.clearFilter;
            emptyStateProps.linkCommand = clearFilterCommand;
            break;
        case 'Flagged':
            mainMessage = strings.flagIsEmpty;
            icon = EMPTY_FLAGGED_SVG;
            emptyStateProps.linkMessage = strings.clearFilter;
            emptyStateProps.linkCommand = clearFilterCommand;
            break;
        case 'ToOrCcMe':
            mainMessage = strings.tomeIsEmpty;
            detailMessage = strings.detailTomeIsEmpty;
            icon = EMPTY_TOME_SVG;
            emptyStateProps.linkMessage = strings.clearFilter;
            emptyStateProps.linkCommand = clearFilterCommand;
            break;
        case 'Mentioned':
            icon = EMPTY_MENTIONS_SVG;
            emptyStateProps.linkMessage = strings.clearFilter;
            emptyStateProps.linkCommand = clearFilterCommand;
            break;
        case 'HasAttachment':
            icon = EMPTY_ATTACHMENTS_SVG;
            emptyStateProps.linkMessage = strings.clearFilter;
            emptyStateProps.linkCommand = clearFilterCommand;
            break;

        // Do not display clear filter command for 'UserCategory' view filter
        case 'UserCategory':
            mainMessage = strings.categoryFolderEmpty;
            break;
    }

    emptyStateProps.mainMessage = mainMessage;
    emptyStateProps.detailMessage = detailMessage;
    emptyStateProps.icon = icon;
}
